import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Mail, Plus, X, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getAuthHeaders } from "@/lib/api";

interface EmailReportDialogProps {
  transactions: any[];
}

export default function EmailReportDialog({ transactions }: EmailReportDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [reportType, setReportType] = useState<"daily" | "weekly" | "custom">("custom");
  const [subject, setSubject] = useState("");
  const { toast } = useToast();

  const sendReportMutation = useMutation({
    mutationFn: async (data: {
      recipients: string[];
      reportType: string;
      transactions: any[];
      subject?: string;
    }) => {
      const response = await fetch("/api/email/send-report", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error("Failed to send report");
      }
      
      return await response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Report Sent",
        description: data.message || "Email report sent successfully",
      });
      setIsOpen(false);
      setRecipients([]);
      setNewEmail("");
      setSubject("");
    },
    onError: (error: any) => {
      toast({
        title: "Send Failed",
        description: error?.message || "Failed to send email report",
        variant: "destructive",
      });
    },
  });

  const addRecipient = () => {
    if (newEmail && newEmail.includes("@") && !recipients.includes(newEmail)) {
      setRecipients([...recipients, newEmail]);
      setNewEmail("");
    }
  };

  const removeRecipient = (email: string) => {
    setRecipients(recipients.filter(e => e !== email));
  };

  const handleSendReport = () => {
    if (recipients.length === 0) {
      toast({
        title: "No Recipients",
        description: "Please add at least one email recipient",
        variant: "destructive",
      });
      return;
    }

    sendReportMutation.mutate({
      recipients,
      reportType,
      transactions,
      subject: subject || `Weighbridge ${reportType} Report - ${new Date().toLocaleDateString()}`
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Mail className="w-4 h-4 mr-2" />
          Email Report
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Email Weighbridge Report</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Report Type */}
          <div className="space-y-2">
            <Label>Report Type</Label>
            <Select value={reportType} onValueChange={(value: any) => setReportType(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily Report</SelectItem>
                <SelectItem value="weekly">Weekly Report</SelectItem>
                <SelectItem value="custom">Custom Report</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`Weighbridge ${reportType} Report - ${new Date().toLocaleDateString()}`}
            />
          </div>

          {/* Email Recipients */}
          <div className="space-y-2">
            <Label>Email Recipients</Label>
            <div className="flex space-x-2">
              <Input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter email address"
                onKeyPress={(e) => e.key === "Enter" && addRecipient()}
              />
              <Button onClick={addRecipient} variant="outline" size="sm">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Display added recipients */}
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {recipients.map((email) => (
                  <Badge key={email} variant="secondary" className="flex items-center gap-1">
                    {email}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 ml-1"
                      onClick={() => removeRecipient(email)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Report Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Report Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Transactions:</span>
                <span className="ml-2 font-medium">{transactions.length}</span>
              </div>
              <div>
                <span className="text-gray-600">Total Weight:</span>
                <span className="ml-2 font-medium">
                  {(transactions.reduce((sum, t) => sum + t.netWeight, 0) / 1000).toFixed(1)}t
                </span>
              </div>
              <div>
                <span className="text-gray-600">Date Range:</span>
                <span className="ml-2 font-medium">
                  {transactions.length > 0 && `${transactions[transactions.length - 1]?.date} - ${transactions[0]?.date}`}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Operators:</span>
                <span className="ml-2 font-medium">
                  {Array.from(new Set(transactions.map(t => t.operator))).length}
                </span>
              </div>
            </div>
          </div>

          {/* Send Button */}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendReport}
              disabled={sendReportMutation.isPending || recipients.length === 0}
            >
              {sendReportMutation.isPending ? (
                "Sending..."
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Report
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}